'use client';
import Head from 'next/head';
import NavBar from '@/components/layouts/NavBar';
import Footer from '@/components/layouts/Footer';
import MethodCard from '@/components/MethodCard';
import { Divide, Grid2x2, Pizza, Shapes, Cylinder, ChartScatter, Omega } from 'lucide-react';
import { TallyIcon, AngleIcon } from '@/components/icons';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

interface ToolEntry {
    icon: React.ReactNode;
    iconColor: string;
    title: string;
    description: string;
    href: string;
    category: string;
    comingSoon: boolean;
}

const methods: ToolEntry[] = [
    {
        icon: <Divide size={18} />,
        iconColor: 'purple',
        title: 'Bus stop method',
        description: 'Solves division questions step-by-step using the Bus Stop method.',
        href: '/methods/bus-stop',
        category: 'method',
        comingSoon: false,
    },
    {
        icon: <Grid2x2 size={18} />,
        iconColor: 'purple',
        title: 'Grid method',
        description: 'Multiply large numbers using a partitioned grid.',
        href: '/methods/grid',
        category: 'method',
        comingSoon: true,
    },
];

const visualisers: ToolEntry[] = [
    {
        icon: <Pizza size={18} />,
        iconColor: 'teal',
        title: 'Pizza slice',
        description: 'Interactive pizza/circle diagram that visualises fractions.',
        href: '/methods/pizza',
        category: 'visualiser',
        comingSoon: false,
    },
    {
        icon: <Shapes size={18} />,
        iconColor: 'amber',
        title: 'Shape visualiser',
        description: 'Generate and customise various mathematical shapes.',
        href: '/methods/shapes',
        category: 'visualiser',
        comingSoon: false,
    },
    {
        icon: <TallyIcon size={18} />,
        iconColor: 'blue',
        title: 'Tally marks',
        description: 'Visualise numbers using tally marks.',
        href: '/methods/tally-marks',
        category: 'visualiser',
        comingSoon: false,
    },
    {
        icon: <AngleIcon size={18} />,
        iconColor: 'coral',
        title: 'Angle visualiser',
        description: 'Visualise angles from 0 to 360 degrees.',
        href: '/methods/angles',
        category: 'visualiser',
        comingSoon: false,
    },
    {
        icon: <Cylinder size={18} />,
        iconColor: 'blue',
        title: 'Cylinder volume',
        description: 'Visualise a 3D cylinder and calculate its volume using V = π × r² × h.',
        href: '/methods/cylinder-volume',
        category: 'visualiser',
        comingSoon: false,
    },
    {
        icon: <ChartScatter size={18} />,
        iconColor: 'teal',
        title: 'Scatter graph',
        description: 'Plot and visualise data points on a coordinate grid.',
        href: '/methods/scatter-graph',
        category: 'visualiser',
        comingSoon: false,
    },
];

const references: ToolEntry[] = [
    {
        icon: <Omega size={18} />,
        iconColor: 'amber',
        title: 'Symbols',
        description: 'Click-to-copy maths, currency, and Greek symbols.',
        href: '/symbols',
        category: 'reference',
        comingSoon: false,
    },
];

function SectionHeading({ id, label }: { id: string; label: string }) {
    return (
        <div className="mb-5 flex items-center gap-3">
            <h2 className="text-[11px] font-medium tracking-[0.05em] text-hint">{label}</h2>
            <div className="h-[0.5px] flex-1 bg-line" />
        </div>
    );
}

export default function HomePage() {
    const router = useRouter();
    const { section } = router.query;
    const [searchQuery, setSearchQuery] = useState('');

    const matches = (tool: ToolEntry) =>
        tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase());

    const filteredMethods = methods.filter(matches);
    const filteredVisualisers = visualisers.filter(matches);
    const filteredReferences = references.filter(matches);

    const hasResults =
        filteredMethods.length > 0 ||
        filteredVisualisers.length > 0 ||
        filteredReferences.length > 0;

    useEffect(() => {
        if (section) {
            const element = document.getElementById(section as string);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [section]);

    const grid = 'grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3';

    return (
        <div className="flex min-h-screen flex-col">
            <Head>
                <title>Maths Tools</title>
            </Head>

            <NavBar />

            <main className="flex-1">
                {/* Hero */}
                <section className="border-b-[0.5px] border-line bg-white px-4 py-10 text-center sm:px-6 sm:py-12">
                    <div className="mx-auto max-w-[1200px]">
                        <span className="mb-4 inline-flex items-center rounded-sm bg-purple-soft px-2 py-0.5 text-[11px] font-medium tracking-[0.02em] text-purple-ink">
                            Fully client-side · No account needed
                        </span>
                        <h1 className="mb-2 text-[26px] font-medium">Explore maths tools</h1>
                        <p className="mx-auto mb-6 max-w-[500px] text-sm text-muted">
                            Interactive methods and visualisers to help you understand and solve
                            mathematical problems.
                        </p>
                        <div className="mx-auto max-w-[400px]">
                            <input
                                type="text"
                                placeholder="Search tools..."
                                aria-label="Search tools"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border-none bg-page"
                            />
                        </div>
                    </div>
                </section>

                {/* Methods */}
                {filteredMethods.length > 0 && (
                    <section id="methods" className="px-4 py-9 sm:px-6">
                        <div className="mx-auto max-w-[1200px]">
                            <SectionHeading id="methods" label="METHODS" />
                            <div className={grid}>
                                {filteredMethods.map((tool) => (
                                    <MethodCard key={tool.title} {...tool} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Visualisers */}
                {filteredVisualisers.length > 0 && (
                    <section id="visualisers" className="px-4 pb-9 sm:px-6">
                        <div className="mx-auto max-w-[1200px]">
                            <SectionHeading id="visualisers" label="VISUALISERS" />
                            <div className={grid}>
                                {filteredVisualisers.map((tool) => (
                                    <MethodCard key={tool.title} {...tool} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* References */}
                {filteredReferences.length > 0 && (
                    <section id="references" className="px-4 pb-9 sm:px-6">
                        <div className="mx-auto max-w-[1200px]">
                            <SectionHeading id="references" label="REFERENCES" />
                            <div className={grid}>
                                {filteredReferences.map((tool) => (
                                    <MethodCard key={tool.title} {...tool} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* No results */}
                {!hasResults && (
                    <section className="px-4 py-16 text-center sm:px-6">
                        <div className="mx-auto max-w-[1200px]">
                            <p className="text-sm text-muted">
                                No tools found matching &quot;{searchQuery}&quot;
                            </p>
                            <button
                                onClick={() => setSearchQuery('')}
                                className="mt-4 text-[13px]"
                            >
                                Clear search
                            </button>
                        </div>
                    </section>
                )}
            </main>

            <Footer />
        </div>
    );
}
